import ArticleIcon from '@mui/icons-material/Article';
import EventIcon from '@mui/icons-material/Event';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import PersonIcon from '@mui/icons-material/Person';
import { DialogContent, DialogTitle, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { format } from 'date-fns';
import { FormStructure, global_utils } from '../../../../backbone-unorganized/shared/forms';
import { CordovaPlugins, getZencamLocalizedStrings } from '../../../../shared/CordovaPlugins';
import { isAndroid, isConnected, isWeb, isWindows } from '../../../../shared/utils/is';
import { showOfflineMessage, showErrorMessage } from '../../../../shared/utils/notifications';
import { ReactPageWrapper } from '../../../shared/views/ReactPageWrapper';
import { TitleImagesAndPDFs } from './models';
import { Gallery } from '@zenput/feature-zencam';

const INSTRUCTIONS_WIDTH_BREAKPOINT = 550;

/**
 * A single image (or PDF) for a list of title images.
 *
 * @see TitleImages
 */
export const TitleImagesItem = Backbone.Marionette.ItemView.extend({
    template: '#tpl_title_images_image',
    model: null, // Image
    tagName: 'a',
    attributes: function () {
        return {
            href: '#',
            class: this._is_file() ? 'view_pdf btn full_btn btn_floating' : 'thumbnail',
            style: this._is_file() ? '' : `background-image: url('${this.templateHelpers().thumbnail_url}')`
        };
    },
    // Used to decide weather the instructions data should be displayed as an image or as a file button
    _is_file: function () {
        if (!this.model) {
            return false;
        }

        if (this.model.is_image_data_uri()) {
            return false;
        }

        var ext = $f.storage.get_file_extension(this.model.get('url'));
        return !_.contains($f.utils.ACCEPTED_INSTRUCTION_FIELD_IMAGE_EXTENSIONS, ext);
    },

    events: {
        click: 'on_click'
    },

    templateHelpers: function () {
        var thumbnail_url = $f.storage.get_thumbnail(this.model.get_full_url(), 234);
        if ($f.storage.is_data_uri(this.model.get_full_url())) {
            thumbnail_url = this.model.get_full_url();
        }
        return {
            thumbnail_url: thumbnail_url,
            is_file: this._is_file()
        };
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        // Files are longer buttons and need their own. Let's break the float:left if so
        if (this._is_file()) {
            this.$el.addClass('clear');
        }
    },

    InlineDetails: function ({ model }) {
        const metadata = model.get('metadata');

        if (!metadata) {
            return null;
        }

        const dateSubmittedEpoch = metadata.get('form_submission').date_submitted.$date;

        if (!dateSubmittedEpoch) {
            return null;
        }

        return (
            <Typography variant="body2" color="gray" textAlign="center" padding={1}>
                {format(new Date(dateSubmittedEpoch), 'MMM dd, yyyy - h:mm aaaa')}
            </Typography>
        );
    },

    ExpandedDetails: function ({ model }) {
        const metadata = model.get('metadata');

        if (!metadata) {
            return null;
        }

        const formSubmission = metadata.get('form_submission');

        const dateSubmittedEpoch = formSubmission.date_submitted.$date;
        const dateSubmittedFormatted = format(new Date(dateSubmittedEpoch), 'MMM dd, yyyy - h:mm aaaa');
        const submitter = formSubmission.created_by.display_name;
        const location = metadata.get('account').title;
        const formTitle = formSubmission.title;

        if (!dateSubmittedEpoch) {
            return null;
        }

        return (
            <>
                <DialogTitle>{_t('mobile_zencam_details_drawer_header')}</DialogTitle>
                <DialogContent sx={{ marginBottom: 2 }}>
                    <List>
                        <ListItem>
                            <ListItemIcon>
                                <FmdGoodIcon arial-label={_t('mobile_common_location')} />
                            </ListItemIcon>
                            <ListItemText primary={location} />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <PersonIcon arial-label={_t('mobile_zencam_submitted_by')} />
                            </ListItemIcon>
                            <ListItemText primary={submitter} />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <ArticleIcon arial-label={_t('mobile_zencam_form_title')} />
                            </ListItemIcon>
                            <ListItemText primary={formTitle} />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <EventIcon arial-label={_t('mobile_zencam_date_submitted')} />
                            </ListItemIcon>
                            <ListItemText primary={dateSubmittedFormatted} />
                        </ListItem>
                    </List>
                </DialogContent>
            </>
        );
    },

    on_click: function (event) {
        event.preventDefault();
        event.stopPropagation();

        if (!isConnected()) {
            return showOfflineMessage();
        }

        if (this._is_file()) {
            if (isWeb() || isWindows()) {
                window.open(this.model.get_full_url(), '_blank');
                return false;
            }
            var url = this.model.get('url');
            var fullUrl = $f.storage.get_storage_full_url(url);
            var filename = url.substr(url.lastIndexOf('/') + 1);

            // @todo @jira:ZP-13581 there's probably a better Capacitor plugin we could use for this
            CordovaPlugins().zencam.viewDocumentRemote(
                {
                    path: fullUrl,
                    original_name: filename
                },
                function () {},
                function (error) {
                    showErrorMessage('Unable to open ' + filename);
                    $f.mobile.logging.log_error('Error opening document', JSON.stringify(error));
                }
            );
            return false;
        }

        if (isWeb()) {
            const imageModelToItem = (model) => ({
                src: model.get_full_url(),
                title: '',
                InlineDetails: model.get('metadata') && (() => <this.InlineDetails model={model} />),
                ExpandedDetails: model.get('metadata') && (() => <this.ExpandedDetails model={model} />)
            });

            const galleryItems = this.model.collection.map(imageModelToItem);

            const startIndex = this.model.collection.indexOf(this.model);

            // On the gallery page, we don't actually know what the total count is, so we'll always fetch when we reach the end of our list
            const totalCount = this.isGallery ? Infinity : this.model.collection.length;

            const fetchMore = () => {
                const imagesCollection = this.model.collection;
                imagesCollection.paginator.next_page();

                const previousLastIndex = imagesCollection.length - 1;

                return new Promise((resolve, reject) => {
                    imagesCollection.fetch({
                        remove: false,
                        success: () => {
                            const newItems = imagesCollection
                                .filter((_, i) => i > previousLastIndex) // only return the new items
                                .map(imageModelToItem);

                            resolve(newItems);
                        },
                        error: () => {
                            imagesCollection.paginator.previous_page();
                            reject();
                        }
                    });
                });
            };

            $f.mobile.nav.show_modal(ReactPageWrapper, {
                renderFunction: function () {
                    return (
                        <Gallery
                            initialItems={galleryItems}
                            startIndex={startIndex}
                            totalCount={totalCount}
                            fetchMore={fetchMore}
                            onGoBack={() => {
                                this.close(); // `this` is `ReactPageWrapper` here
                            }}
                            translatedStrings={{
                                loadError: _t('mobile_dialog_image_preview_message'),
                                deleteDialog: {
                                    title: _t('mobile_drafts_button_delete'),
                                    description: _t('mobile_message_image_confirm_remove_image'),
                                    confirmLabel: _t('mobile_drafts_button_delete'),
                                    cancelLabel: _t('mobile_action_sheet_cancel')
                                }
                            }}
                        />
                    );
                }
            });
            return;
        }

        var files_array = [];
        var image_index = 0;
        var selected_photo_url = this.model.get_full_url(true);

        this.model.collection.each(function (model, i) {
            var photo_url = model.get_full_url(true);

            files_array.push({ url: photo_url, metadata: model.get('metadata') });

            if (selected_photo_url === photo_url) {
                image_index = i;
            }
        });

        if (!this.model.get('metadata')) {
            CordovaPlugins().zencam.getZencam(
                function (urls) {
                    // NOTE(Nick) - The success function for zencam gallery passes back a dictionary of
                    // modified and deleted image urls. This use case has view-only photos so nothing needs to
                    // be done when the user exits the gallery.
                },
                function (error) {
                    $f.mobile.logging.log_error('Error while getting zencam plugin', error);
                },
                {
                    destinationType: CordovaPlugins().zencam.DestinationType.FILE_URI,
                    encodingType: CordovaPlugins().zencam.EncodingType.JPEG,
                    inputType: CordovaPlugins().zencam.InputType.FILE_ARRAY,
                    inputData: files_array,
                    upperBound: 1000,
                    allowEdit: false, // @author: halah this not even used
                    allowDelete: false,
                    strings: getZencamLocalizedStrings(),
                    selectedImageIndex: image_index,
                    compressionPercent: 90 // @author:david This should be 100 because we don't want to re-compress,
                    // but some Android devices will lock up to an infinite loop if compression
                    // is 100. Setting to 90 seems to work fine.
                    // @author: halah since compressionPercent value is always sent as 90, refactor not to be sent at all
                    // upperBound is always sent as 1000, refactor to not send it at all
                }
            );
        } else {
            var success_callback = function () {};

            var error_callback = function (error) {
                $f.mobile.logging.log_error('Error while getting zencam plugin', error);
            };

            if (isAndroid()) {
                CordovaPlugins().zencam.openGallery(
                    {
                        images: files_array,
                        selectedImageIndex: image_index,
                        strings: getZencamLocalizedStrings(),
                        request: {
                            base_url: API_URL,
                            storage_base_url: $f.storage.get_storage_base_url(),
                            endpoint_url: `${this.model.collection.url}/search/`,
                            start: this.model.collection.paginator.get('start'),
                            limit: this.model.collection.paginator.get('limit'),
                            body: JSON.stringify({
                                search_criteria: JSON.stringify(this.model.collection.search_criterias),
                                dashboard_filters: JSON.stringify(this.model.collection.dashboard_filters),
                                order_by: 'name',
                                tz: $f.user.time_zone,
                                search_by: 'account',
                                select_all: false
                            })
                        }
                    },
                    success_callback,
                    error_callback
                );
            } else {
                CordovaPlugins().zencam.openGallery(
                    {
                        images: files_array,
                        selectedImageIndex: image_index,
                        strings: getZencamLocalizedStrings(),
                        request: {
                            base_url: API_URL,
                            endpoint_url: `${this.model.collection.url}/search/`,
                            storage_base_url: $f.storage.get_storage_base_url(),
                            body: this.model.collection.to_endcoded_URI(),
                            start: this.model.collection.paginator.get('start'),
                            limit: this.model.collection.paginator.get('limit')
                        }
                    },
                    success_callback,
                    error_callback
                );
            }
        }

        return false;
    }
});

/**
 * A list of title images (or PDFs) for a given model containing a 'title_image' attribute
 * (i.e., a form field or a task model).
 */
export const TitleImages = Backbone.Marionette.CollectionView.extend({
    collection: null, // Images
    itemView: TitleImagesItem,

    // Passed in from GalleryImagesList when we're on the gallery page
    isGallery: false,

    className: 'thumbnails',

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        if (!this.collection) {
            throw new Error('Programmer Error: collection required for TitleImages');
        }
    },

    onRender: function () {
        this.$el.append('<div class="clear"></div>');
    }
});

/**
 * The instructions and title images parent view for individual form fields.
 */
export const FieldInstructionsAndTitleImages = Backbone.Marionette.Layout.extend({
    template: '#tpl_field_instructions_and_title_images',
    model: null, // MobileBaseField

    regions: {
        r_title_images: '.title_images'
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
    },

    onRender: function () {
        this.r_title_images.show(
            new TitleImages({
                collection: new TitleImagesAndPDFs(this.model.get('title_image'))
            })
        );
    }
});

/**
 * Our magical base field.  This is responsible for all the shared code
 * between the different types of fields.  All others should inherit
 * from this class
 */
export const BaseFieldView = Backbone.Marionette.Layout.extend({
    tagName: 'li',
    className: 'form-field',
    _is_instructions_expanded: false,

    getTemplate: function () {
        return '#tpl_' + this.model.get('type') + '_field';
    },

    // @todo @author:david we send our model as a templateHelper for legacy. Until our
    // templates reference model attributes directly instead of calling model.get('foobar'), we need this.
    templateHelpers: function () {
        return { model: this.model };
    },

    triggers: {
        request_error_wizard_recalculation_noop: 'request_error_wizard_recalculation',
        prevent_next_reload_on_resume_noop: 'prevent_next_reload_on_resume'
    },

    regions: {
        // do not define regions here in base class so extended views don't have to call super.
        // Instead, define in initialize().
    },

    events: {
        'keyup textarea': 'expand_textarea'
        // @see constructor() for toggle_instructions_title_images_display event
    },

    constructor: function (attributes, options) {
        this.regions.r_instructions = '.field_instructions';

        BaseFieldView.__super__.constructor.apply(this, arguments);

        // can't be put directly in class `events` object because most of our subclasses override it
        if (!this.events['click .expand_instructions_btn']) {
            this.events['click .expand_instructions_btn'] = 'toggle_instructions_title_images_display';
        }

        // Re-render when our model's value has been removed (necessary for image fields on mobile web)
        this.listenTo(this.model, 'field:value_cleared', function () {
            this.render();
        });
    },

    render: function () {
        var this_ = this;

        if ($(this.template).length == 0) {
            this.$el.hide();
        } // hide any incompatible templates, i.e. features

        BaseFieldView.__super__.render.apply(this, arguments);

        this.$el.removeClass('invalid'); // assume all fields are valid when first showing them (helps when resuming from account picker, etc.)
        if (
            this.model.get('value') &&
            !_.contains(
                ['image', 'video', 'email', 'ingredient_temperature', 'equipment_temperature'],
                this.model.get('type')
            )
        ) {
            this.$el.find('input, textarea, .fake_input, select').addClass('has_value');
        }

        // Expand any textareas in the view for the first time
        if (this.$el.find('textarea').length > 0) {
            const textarea = this.$el.find('textarea').get(0);

            textarea.addEventListener('input', function () {
                // max length for text value in ES is 32766 bytes, so truncating to pass indexing: ZP-14923
                if (textarea.value.length > 8000) {
                    textarea.value = textarea.value.substring(0, 8000);
                    showErrorMessage(_t('mobile_field_text_limit_error'));
                }
            });

            setTimeout(() => {
                this.expand_textarea({ target: textarea });
            }, 50);
        }

        this.$el.find('.field_title a, .instructions_field_instructions a').attr('target', '_blank');

        // show instructions/title_image region, if applicable
        if (
            this.model.get('type') !== 'section' &&
            (this.model.get('instructions') || (this.model.get('title_image') && this.model.get('title_image').length))
        ) {
            this.$el
                .find('.instructions_field_instructions, .field_title')
                .first()
                .prepend(
                    '<a href="#" class="expand_instructions_btn">' +
                        '<i class="df-info"></i>' +
                        '<i class="df-remove-circle"></i>' +
                        '</a>'
                );

            // unfortunately we need to re-delegate events to handle the above HTML
            this.delegateEvents();

            // auto-open instructions field if specified but not already filled out
            if (
                this.model.get('metadata') &&
                this.model.get('metadata').expand_instructions &&
                !this.model.has_value()
            ) {
                this._show_instructions(true);
            }
        }

        // @author: kgstew we are checking if this has a proper collection to prevent interference with the
        // TriggeringImageFieldsList This Collection view is using a BaseField model which relies on
        // being attached to a FormStructure collection but TriggeringImageFieldsList uses an arbitrary
        // collection that tries to render BaseFields. This sucks, we should fix it.
        if (this.model.is_dependent_field() && this.model.collection instanceof FormStructure) {
            var dependent_field_level = this.model.get_dependent_field_level();
            this.$el.addClass('dependent_field');
            this.$el.addClass('dependent_field_level_' + dependent_field_level);

            // Create an array of dependent_field_level length with empty values
            // Iterate over and use the index to set the level of the div
            _.each(new Array(dependent_field_level), function (_, i) {
                this_.$el.prepend('<div class="dependent_vertical_line_level_' + (i + 1) + '" />');
            });
        }

        if (this.model.get('visible') === false) {
            this.$el.addClass('not_visible');
        }
    },

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        this.template = this.getTemplate();

        this.listenTo(this, 'dom:refresh', this.set_element_id);
        this.listenTo(this.model, 'change', this.handle_change);

        this.validate_template();

        // whenever a model changes, we should warn the user if it's invalid.
        this.listenTo(this.model, 'change', function (model) {
            model.validate();
        });

        Backbone.Validation.unbind(this);
        Backbone.Validation.bind(this);
        this.model.unbind('validated:invalid');
        this.model.bind('validated:invalid', function (model, error) {
            this_.on_model_invalid.apply(this_, arguments);
        });
        this.model.unbind('validated:valid');
        this.model.bind('validated:valid', function (model) {
            this_.on_model_valid.apply(this_, arguments);
        });

        // set model.is_valid for future access, @see on_model_valid()/on_model_invalid()
        this.model.is_valid = this.model.isValid(true, true);
    },

    validate_template: function () {
        // FOR SAFETY - check to see if there is actually a template.  this helps with backwards campatability
        if ($(`#tpl_${this.model.get('type')}_field`).length == 0) {
            this.model.set('not_compatible', true); // this is to allow our validation to skip this
        }
    },

    set_element_id: function () {
        this.$el.prop('id', this.get_element_id());
    },

    get_element_id: function () {
        // Element `id` *must* be unique, we have duplication in child node so we need to ensure that's not happening
        if (this.$el.parent().closest('li.form-field').length) {
            return `field_${this.model.get('id')}_${_.uniqueId()}`;
        }
        return `field_${this.model.get('id')}`;
    },

    /**
     * Handle invalidated or invalidated fields. These two methods will:
     *
     *  - Add an .invalid class on the field HTML to represent an invalid field to the user
     *  - Mark our own is_valid flag as true/false
     *  - Trigger an event to request that the collection view's error wizard recalculate the total
     *      number of invalid fields.
     *
     * @note @author:david the collection view cannot just rely on the change/valid/invalid event itself, because
     * we need to guarantee the error recalculation occurs AFTER us (that is, model.is_valid is set).
     */

    on_model_invalid: function (model, error) {
        this.model.is_valid = false;
        this.$el.addClass('invalid');
        this.trigger('request_error_wizard_recalculation', this.model);
    },

    on_model_valid: function (model) {
        this.model.is_valid = true;
        this.$el.removeClass('invalid');
        this.trigger('request_error_wizard_recalculation', this.model);
    },

    /**
     * Due to the way forms work for the mobile web, we cannot redraw the file input
     * fields.  Stop them from redrawing here
     * Note: this occurs after perform_event on the model (which sets visibility for related fields)
     */
    handle_change: function (model) {
        this.on_change_action(model);
        if (isWeb() && model.get('type') == 'image') {
            return;
        }
        this.render();
    },

    /**
     * A way for certain view subclasses to add specific behaviors (scrolling etc) after inputs are changed.
     */
    on_change_action: function (model) {
        return;
    },

    save_value: function (event) {
        var input = $(event.target).val();
        this.$el.removeClass('invalid'); // innocent until proven guilty
        this.model.set({ value: input });
    },

    toggle_instructions_title_images_display: function (event) {
        this._is_instructions_expanded ? this._hide_instructions() : this._show_instructions();
        return false;
    },

    _show_instructions: function (no_animation) {
        this._is_instructions_expanded = true;

        if (!this.$el.find('.field_instructions').length) {
            // if we are in the wide layout (i.e., tablet), we'll place the instructions
            // after both the title and value. Otherwise, instructions will go under the title.
            this.$el
                .find(
                    `.${
                        $(window).outerWidth() > INSTRUCTIONS_WIDTH_BREAKPOINT ? 'field_value' : 'field_title'
                    }, .instructions_field_instructions`
                )
                .first()
                .after('<div class="clear"></div><div class="field_instructions"></div>');
        }

        if (!this.r_instructions.currentView) {
            this.r_instructions.show(new FieldInstructionsAndTitleImages({ model: this.model }));
        }

        this.$el.find('.expand_instructions_btn').addClass('expanded');

        this.$el.find('.field_instructions .field_instructions_text').find('a').attr('target', '_blank');

        if (no_animation) {
            this.$el.find('.field_instructions').show();
            return;
        }

        this.$el.find('.field_instructions').velocity('slideDown', { duration: 200 });
    },

    _hide_instructions: function () {
        this._is_instructions_expanded = false;
        this.$el.find('.expand_instructions_btn').removeClass('expanded');
        this.$el.find('.field_instructions').velocity('slideUp', { duration: 200 });
    },

    /**
     * Scroll down the height of the calling fieldView
     * @private
     */
    _scroll_down_one_field: function () {
        const animationSpeed = 400;
        const maxTopDistance = 120; // We usually have sticky header
        const $target = this.$el;

        if (!$target) {
            return;
        }
        const scrollDistance = $target.outerHeight();
        const $pageScrollable = $('.page_scrollable');
        if ($pageScrollable.length === 0) {
            return;
        }
        const { top: pageScrollableTop } = $pageScrollable[0].getBoundingClientRect();
        const { top: elementTop } = this.el.getBoundingClientRect();

        if (elementTop - pageScrollableTop < maxTopDistance) {
            return;
        }
        const destination = $pageScrollable.scrollTop() + scrollDistance;

        // this is getting called repeatedly because BaseFieldView._handle_change() is getting called multiple times
        // (and likely other reasons, depending on the field type). We can prevent jank from animate()s by calling stop():
        $pageScrollable.stop();

        $pageScrollable.animate(
            {
                scrollTop: destination
            },
            animationSpeed
        );
    },

    expand_textarea: global_utils.expand_textarea,

    /**
     * Get css class based off of acceptance_criteria to color the input field
     */
    _get_acceptance_criteria_class: function () {
        if (this.model.get('acceptance_value') === 1) {
            return 'acceptance_value_pass';
        } else if (this.model.get('acceptance_value') === -1) {
            return 'acceptance_value_failure';
        } else if (this.model.get('acceptance_value') === 0) {
            return 'acceptance_value_neutral';
        } else {
            return '';
        }
    }
});
